import React, { Fragment, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useToggle } from 'react-use';
import { selectUserPreferences } from 'redux/selectors';
import { selectUserPreference } from 'redux/UserPreferences';
import { EditIcon, TrashIcon } from 'components/icons';
import IconButton from 'components/shared/IconButton';
import ToggleButton from 'components/shared/ToggleButton';
import { PreferenceNameEditor } from '../PreferenceNameEditor';
import { ChartSetPreferenceState } from '../ChartSetPreferenceState';
import ChartSetPreferenceExport from '../ChartSetPreferenceExport/ChartSetPreferenceExport';
import styles from './ChartSetPreferenceListItem.module.css';

function ChartSetPreferenceListItem({ preferenceId, defaultPreferenceId, onUpdateName, onToggleDefault, onDelete }) {
  const preference = useSelector((state) => selectUserPreference(selectUserPreferences(state), preferenceId));
  const [editMode, toggleEditMode] = useToggle(false);
  const isPreferenceLocal = preference?.local || false;
  const isDefaultPreference = preference.default || preferenceId === defaultPreferenceId;

  const handleUpdate = useCallback(
    (values) => {
      onUpdateName(values);
      toggleEditMode();
    },
    [onUpdateName, toggleEditMode]
  );
  const handleToggleDefault = useCallback(() => {
    onToggleDefault(preference);
  }, [onToggleDefault, preference]);
  const handleDelete = useCallback(() => {
    onDelete(preference);
  }, [onDelete, preference]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {editMode ? (
          <PreferenceNameEditor preference={preference} onSubmit={handleUpdate} onCancel={toggleEditMode} />
        ) : (
          <Fragment>
            <div className={styles.title}>
              {preference.name}
              {isPreferenceLocal ? ' (System Default)' : ''}
            </div>
            {!isPreferenceLocal && (
              <IconButton
                aria-label="Edit"
                variant="link"
                color="secondary"
                size="x-small"
                onClick={() => toggleEditMode(true)}
              >
                <EditIcon />
              </IconButton>
            )}
          </Fragment>
        )}
        <ToggleButton className={styles.defaultToggle} checked={isDefaultPreference} onChange={handleToggleDefault}>
          Make default
        </ToggleButton>
      </div>
      <div className={styles.body}>
        <ChartSetPreferenceState preference={preference} />
        <div className={styles.actions}>
          <ChartSetPreferenceExport chartSet={preference} />
          <IconButton
            aria-label="Delete"
            data-tip="Delete Chart Set"
            variant="text"
            color="primary"
            size="x-small"
            disabled={isPreferenceLocal}
            onClick={handleDelete}
          >
            <TrashIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}

export default ChartSetPreferenceListItem;
